/**
 * PostHog — adoption produit du PORTAIL CLIENT (client.heyanto.fr) — LOT P4.
 *
 * Calqué sur `apps/web/src/lib/posthog.ts` (PWA artisans) : le SDK n'est JAMAIS
 * importé de façon eager. `posthog-js` (~200 Ko) est chargé à la demande via un
 * import() dynamique mis en cache, déclenché par `initPosthog()` depuis main.tsx.
 * Toutes les fonctions publiques sont fire-and-forget (retour void) et no-op sûres :
 * si le traçage est désactivé (dev local, kill switch), le SDK n'est même pas
 * téléchargé.
 *
 * ⚠️ PALIER 1 — **LE TRAÇAGE EST DÉSACTIVÉ PAR DÉFAUT.** Le portail est utilisé
 * par les CLIENTS de l'artisan (des tiers, pas nos utilisateurs payants) : tant
 * que les durées de conservation, l'information RGPD et le registre des
 * sous-traitants ne sont pas cadrés, rien ne part. L'activation est un acte
 * explicite : `VITE_POSTHOG_ENABLED=true` au build. Sans ce drapeau, aucun octet
 * du SDK n'est même téléchargé.
 *
 * Configuration (variables Vite, toutes optionnelles) :
 *  — VITE_POSTHOG_ENABLED   INTERRUPTEUR PRINCIPAL : "1"/"true" → traçage autorisé.
 *                           Absent/vide → tout le module est no-op.
 *  — VITE_POSTHOG_KEY       clé projet publique (défaut : projet ANTÒ ci-dessous)
 *  — VITE_POSTHOG_HOST      ingestion (défaut : PostHog Cloud EU)
 *  — VITE_POSTHOG_DISABLED  kill switch : "1"/"true" → tout est no-op même si
 *                           VITE_POSTHOG_ENABLED est posé
 *  — VITE_POSTHOG_FORCE     "1"/"true" → active AUSSI hors build de production
 *                           (sinon `import.meta.env.PROD` seul autorise l'init)
 *
 * ⚠️ DIFFÉRENCE ASSUMÉE avec la PWA : **AUCUN session replay ici**, et **AUCUNE
 * donnée personnelle dans `identify`** — ni nom, ni email : le portail affiche
 * les factures, tarifs et contrats de tiers. Seuls partent l'identifiant technique
 * du client, l'identifiant de son tenant et la surface.
 * `person_profiles: "identified_only"` : aucun profil pour un visiteur non connecté.
 */
import type { PostHog } from "posthog-js";

/** Clé projet publique ANTÒ (PostHog Cloud EU) — surchargeable par VITE_POSTHOG_KEY. */
const DEFAULT_KEY = "phc_u73LGsvGtvKXMmWVbDBHiMZbnbynNdL8dkErp57jkP29";
/** Ingestion PostHog Cloud EU — surchargeable par VITE_POSTHOG_HOST. */
const DEFAULT_HOST = "https://eu.i.posthog.com";

/** Super-propriété présente sur TOUS les événements du portail. */
const SURFACE = "portal";

/** Lit une variable Vite en string (les booléens `PROD`/`DEV` sont lus à part). */
function envString(name: string): string | undefined {
  const bag = import.meta.env as unknown as Record<string, unknown>;
  const raw = bag[name];
  return typeof raw === "string" ? raw : undefined;
}

/** "1"/"true"/"yes" → true ; "", "0", "false", absent → false. */
function envFlag(name: string): boolean {
  const raw = (envString(name) ?? "").trim().toLowerCase();
  if (raw === "" || raw === "0" || raw === "false" || raw === "no") return false;
  return true;
}

/** Clé projet effective (env > défaut). */
export function posthogKey(): string {
  return envString("VITE_POSTHOG_KEY")?.trim() || DEFAULT_KEY;
}

/** Host d'ingestion effectif (env > défaut). */
export function posthogHost(): string {
  return envString("VITE_POSTHOG_HOST")?.trim() || DEFAULT_HOST;
}

/**
 * Le traçage est-il actif ? Gate unique de TOUT le module. Lue à CHAQUE appel
 * (et non figée au chargement) pour rester pilotable en test.
 */
export function isPosthogEnabled(): boolean {
  if (typeof window === "undefined") return false;
  // Palier 1 : opt-in explicite. Aucun traçage tant que ce drapeau est absent.
  if (!envFlag("VITE_POSTHOG_ENABLED")) return false;
  if (envFlag("VITE_POSTHOG_DISABLED")) return false;
  if (!posthogKey()) return false;
  return import.meta.env.PROD === true || envFlag("VITE_POSTHOG_FORCE");
}

/** Promesse de chargement + init du SDK (null si le chargement a échoué). */
let initPromise: Promise<PostHog | null> | null = null;
/** Identité en attente, transmise avant que le SDK soit prêt — rejouée à l'init. */
let pendingIdentity: { client: PhPortalClient; tenantId?: string | null } | null = null;
/** `posthog.init()` capture déjà le 1er $pageview — on saute le premier appel manuel. */
let initialPageviewConsumed = false;

/**
 * Client du portail. Seul l'identifiant est déclaré : le nom et l'email sont
 * volontairement HORS du contrat de ce module pour qu'aucune PII ne puisse y
 * entrer par inadvertance.
 */
export interface PhPortalClient {
  /** Identifiant du client en base — distinct_id PostHog (pseudonyme). */
  id: string;
}

/**
 * Applique `fn` dès que le SDK est prêt. Déclenche l'init si elle n'a pas encore eu
 * lieu : un événement émis avant l'init de main.tsx n'est donc jamais perdu.
 */
function withPosthog(fn: (ph: PostHog) => void): void {
  if (!isPosthogEnabled()) return;
  if (!initPromise) initPosthog();
  if (!initPromise) return;
  void initPromise
    .then((ph) => {
      if (ph) fn(ph);
    })
    .catch(() => {
      /* non bloquant — l'analytics ne doit jamais casser le portail */
    });
}

/**
 * Pousse l'identité (identify + group tenant) sur une instance prête.
 * SANS PII : le nom et l'email du client ne quittent JAMAIS le portail — seul
 * l'identifiant technique (pseudonyme), le tenant et la surface sont transmis.
 */
function applyIdentity(ph: PostHog, client: PhPortalClient, tenantId?: string | null): void {
  ph.identify(client.id, {
    ...(tenantId ? { tenant_id: tenantId } : {}),
    surface: SURFACE,
  });
  // L'« enseigne » du portail = le tenant fournisseur choisi à la connexion.
  if (tenantId) ph.group("tenant", tenantId);
}

/**
 * Charge et initialise PostHog (idempotent). No-op complet hors production sans
 * VITE_POSTHOG_FORCE=1 — aucun octet de SDK n'est téléchargé en dev local.
 */
export function initPosthog(): void {
  if (!isPosthogEnabled() || initPromise) return;

  initPromise = import("posthog-js")
    .then(({ default: posthog }) => {
      posthog.init(posthogKey(), {
        api_host: posthogHost(),
        // Vue de page initiale automatique ; les changements de route SPA sont
        // remontés manuellement par phPageview() (cf. App.tsx).
        capture_pageview: true,
        // On instrumente explicitement (phCapture) : pas de clics aveugles.
        autocapture: false,
        // Aucun profil personne tant que le client ne s'est pas connecté.
        person_profiles: "identified_only",
        // JAMAIS de replay sur le portail : ce sont les documents de tiers.
        disable_session_recording: true,
        loaded: (ph) => {
          try {
            ph.register({ surface: SURFACE });
          } catch {
            /* non bloquant */
          }
        },
      });
      // Rejoue l'identité transmise avant que le SDK soit prêt.
      if (pendingIdentity) {
        applyIdentity(posthog, pendingIdentity.client, pendingIdentity.tenantId);
        pendingIdentity = null;
      }
      return posthog;
    })
    .catch(() => null);
}

/**
 * Identifie le client connecté et le rattache au groupe « tenant » (son fournisseur).
 * Appelé au login ET à la réhydratation de session depuis localStorage.
 */
export function phIdentify(
  client: PhPortalClient | null | undefined,
  tenantId?: string | null,
): void {
  if (!isPosthogEnabled() || !client?.id) return;
  pendingIdentity = { client, tenantId };
  withPosthog((ph) => {
    if (!pendingIdentity) return;
    applyIdentity(ph, pendingIdentity.client, pendingIdentity.tenantId);
    pendingIdentity = null;
  });
}

/** Événement produit explicite. Fire-and-forget, no-op sûr si le traçage est off. */
export function phCapture(event: string, props?: Record<string, unknown>): void {
  if (!isPosthogEnabled() || !event) return;
  withPosthog((ph) => ph.capture(event, props));
}

/**
 * Vue de page sur changement de route (React Router). Le tout premier appel est
 * ignoré : `posthog.init()` a déjà émis le $pageview du chargement initial.
 */
export function phPageview(): void {
  if (!isPosthogEnabled()) return;
  if (!initialPageviewConsumed) {
    initialPageviewConsumed = true;
    return;
  }
  withPosthog((ph) => ph.capture("$pageview"));
}

/** Coupe le lien identité ↔ device au logout (nouvel anonyme). */
export function phReset(): void {
  pendingIdentity = null;
  if (!isPosthogEnabled()) return;
  withPosthog((ph) => ph.reset());
}

/** Réinitialise l'état module — RÉSERVÉ AUX TESTS. */
export function __resetPosthogForTests(): void {
  initPromise = null;
  pendingIdentity = null;
  initialPageviewConsumed = false;
}
